import { Injectable, inject } from "@angular/core";
import {
    BehaviorSubject,
    Observable,
    Subject,
    combineLatest,
    merge,
    of
} from "rxjs";
import {
    catchError,
    distinctUntilChanged,
    filter,
    map,
    scan,
    shareReplay,
    switchMap,
    tap,
    withLatestFrom
} from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { DigitalSignaturDeltagareTyper } from "~app/common/models/constants";
import { ApiClientService } from "~app/common/services/api-client.service";
import { CommandService, ICommand } from "~app/common/services/cqs/command.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { CreateAndSendContractCommand } from "../commands/digitalsignering/CreateAndSendContractCommand";
import { ProlongContractCommand } from "../commands/digitalsignering/ProlongContractCommand";
import { SendReminderContractCommand } from "../commands/digitalsignering/SendReminderContractCommand";
import { UpdateSigningStatusCommand } from "../commands/digitalsignering/UpdateSigningStatusCommand";
import { WithdrawContractCommand } from "../commands/digitalsignering/WithdrawContractCommand";
import { RapportUtskriftCreatedEventMessage } from "../kontrakt/kontrakt-utskrift/kontrakt-generera-rapport/rapportUtskriftCreatedEventMessage";
import { IVKDigitalsignaturDeltagare } from "../kontrakt/kontrakt-digitalsignering/kontrakt-signering/kontrakt-signering-model";
import { Kontrakt, VKDigitalsigneringUtskrifterDTO, VirkeskopskontraktDigitalSignering } from "../models/kontrakt-models";
import { UtskriftTyp } from "../models/utskrift/utskrift-models";
import { IKontraktSigneringRequestDTO, dokumentExcludeDict } from "../models/utskrift/utskriftSignering-models";
import { AvtalService } from "./avtal.service";
import { KontraktBilagorService } from "./kontrakt-bilagor.service";
import { KontraktMediatorService } from "./kontrakt-mediator.service";
import { VirkeskopskontraktLeverantorMediatorService } from "./virkeskopskontrakt-leverantor-mediator.service";

@Injectable()
export class DigitalSigneringMediatorService {
    kontraktUtskrifter$: Observable<VKDigitalsigneringUtskrifterDTO>;
    kontraktsSigneringar$: Observable<VirkeskopskontraktDigitalSignering[]>;
    hasAktivSignering$: Observable<boolean>;
    srcRefreshSigneringar$ = new Subject<void>();
    deltagareList$: Observable<IVKDigitalsignaturDeltagare[]>;

    private readonly extraDeltagareList$ = new BehaviorSubject<IVKDigitalsignaturDeltagare[]>([]);
    private readonly apiClientService = inject(ApiClientService);
    private readonly busyService = inject(BusyService);
    private readonly commandService = inject(CommandService);
    private readonly avtalService = inject(AvtalService);
    private readonly virkeskopskontraktLeverantorMediatorService = inject(VirkeskopskontraktLeverantorMediatorService);
    private readonly kontraktBilagorService = inject(KontraktBilagorService);
    private readonly eventService = inject(EventService);

    private readonly createAndSendContractSrc = new Subject<CreateAndSendContractCommand>();
    private readonly createAndSendContract$ = this.createAndSendContractSrc.pipe(
        switchMap(command => this.postCreateAndSendContractSrc(command)),
        map(() => (curr: VirkeskopskontraktDigitalSignering[]) => curr)
    );
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly kontrakt$: Observable<Kontrakt> = this.kontraktMediatorService.kontrakt$.pipe(
        filter(kontrakt => ObjectUtils.hasValue(kontrakt)),
        distinctUntilChanged((a, b) => a.id === b.id)
    );

    private readonly fetchDigitalSignering$ = this.kontrakt$.pipe(
        filter(kontrakt => kontrakt.id > 0),
        tap((kontrakt) => this.kontrakt = kontrakt),
        switchMap(kontrakt => this.getDigitalSignering(kontrakt.id)),
        filter(digitalSignering => !ObjectUtils.isNullOrUndefined(digitalSignering)),
        map(digitalSignering => (_curr: VirkeskopskontraktDigitalSignering[]) => digitalSignering)
    );

    private kontrakt: Kontrakt;

    constructor() {
        const reloadSigneringar$ = merge(
            this.createAndSendContract$,
            this.srcRefreshSigneringar$
        ).pipe(
            switchMap(() => this.fetchDigitalSignering$)
        );

        this.kontraktsSigneringar$ = merge(
            this.fetchDigitalSignering$,
            reloadSigneringar$
        ).pipe(
            scan((acc: VirkeskopskontraktDigitalSignering[], reducer: (curr: VirkeskopskontraktDigitalSignering[]) => VirkeskopskontraktDigitalSignering[]) => reducer(acc),
                {}
            ),
            shareReplay(1)
        );

        // Kollar om det finns aktiva signering via kontrakt för att alla roller inte har tillgång till signeringar
        this.hasAktivSignering$ = this.kontrakt$.pipe(
            map(kontrakt => kontrakt.hasAktivSignering)
        );

        const reloadUtskrift$ = this.kontraktMediatorService.kontrakt$.pipe(
            filter(kontrakt => ObjectUtils.hasValue(kontrakt?.id)),
            switchMap((kontrakt) => this.getDigitalsigneringUtskrifter(kontrakt.id))
        );

        // Ett dokument (Kontrakt, Kontrakt-karta, Kontrakt-betalplan m.fl.) kan skapas om utan att kontraktet byts
        // eller en bilaga ändras. Utan detta blir kontraktUtskrifter$ inaktuell och kan innehålla ett
        // dokument-id som inte längre finns, vilket gör att CreateAndSendContractCommand skickar ett ogiltigt id.
        const rapportUtskriftCreated$ = this.eventService.subscribeEvent<RapportUtskriftCreatedEventMessage>(RapportUtskriftCreatedEventMessage.TYPE).pipe(
            filter(message => message.success),
            withLatestFrom(this.kontrakt$),
            filter(([
                message,
                kontrakt
            ]) => message.kontraktId === kontrakt.id)
        );

        const kontraktDokumentChange$ = merge(
            this.kontraktBilagorService.bilagaChange$,
            rapportUtskriftCreated$
        ).pipe(
            switchMap(() => reloadUtskrift$)
        );

        this.kontraktUtskrifter$ = merge(reloadUtskrift$, kontraktDokumentChange$).pipe(
            shareReplay(1)
        );
        const leverantorer$ = this.virkeskopskontraktLeverantorMediatorService.kontraktLeverantorer$.pipe(
            map(leverantorer => leverantorer.map(k => (
                {
                    deltagarTyp: DigitalSignaturDeltagareTyper.leverantor,
                    epost: k.epost,
                    deltagarGuid: k.leverantorGuid,
                    isPersonnummer: k.isPersonnummer,
                    id: k.id,
                    kontaktPersonMobiltelefon: k.kontaktPersonMobiltelefon,
                    kontaktPersonNamn: k.kontaktPersonNamn,
                    leverantorId: k.leverantorId,
                    makeGodkannandeKravsEj: k.makeGodkannandeKravsEj,
                    namn: k.namn,
                    persOrgNummer: k.persOrgNummer,
                    telefon: k.telefon,
                    inbjudningsOrdning: 1
                }))));
        this.deltagareList$ = combineLatest([
            leverantorer$,
            this.extraDeltagareList$
        ])
            .pipe(map(([leverantorer, extraDeltagare]) => [
                ...leverantorer,
                ...extraDeltagare
            ]));
    }

    addMottagare(value: IVKDigitalsignaturDeltagare) {
        const newList = [
            ...this.extraDeltagareList$.value,
            value
        ];
        this.extraDeltagareList$.next(newList);
    }

    removeMottagare(value: IVKDigitalsignaturDeltagare) {
        this.extraDeltagareList$.next(this.extraDeltagareList$.value.filter(x => x.deltagarGuid !== value.deltagarGuid));
    }

    filterKontraktUtskrifter(dokumentTyp: UtskriftTyp, { utskrifter, ovrigaBilagor }: VKDigitalsigneringUtskrifterDTO): VKDigitalsigneringUtskrifterDTO {
        const exclude = dokumentExcludeDict[dokumentTyp];
        return {
            utskrifter: utskrifter.filter(x => !exclude.utskrifter.includes(x.typ)),
            ovrigaBilagor: ovrigaBilagor.filter(x => !exclude.bilagor.includes(x.typ))
        };
    }

    getKontraktUtskrifter(dokumentTyp: UtskriftTyp): Observable<VKDigitalsigneringUtskrifterDTO> {
        return this.kontraktUtskrifter$.pipe(
            map(utskrifter => this.filterKontraktUtskrifter(dokumentTyp, utskrifter))
        );
    }

    prolongSigning(virkeskopskontraktSigneringId: number, days: number) {
        const command = new ProlongContractCommand(virkeskopskontraktSigneringId, days);
        // Detta command körs alltid isolerat - oavsett vad som ligger i stacken hos commandService
        this.sendIsolatedCommand(command);
    }

    sendContractForSigning(kontraktSignering: IKontraktSigneringRequestDTO) {
        const command = new CreateAndSendContractCommand(this.kontrakt.id, kontraktSignering);
        this.createAndSendContractSrc.next(command);
    }

    sendReminder(virkeskopskontraktSigneringId: number) {
        const command = new SendReminderContractCommand(virkeskopskontraktSigneringId);
        // Detta command körs alltid isolerat - oavsett vad som ligger i stacken hos commandService
        this.sendIsolatedCommand(command);
    }

    updateSigningStatus(virkeskopskontraktSigneringId: number) {
        const command = new UpdateSigningStatusCommand(virkeskopskontraktSigneringId);
        // Detta command körs alltid isolerat - oavsett vad som ligger i stacken hos commandService
        this.sendIsolatedCommand(command);
    }

    withdrawDocument(virkeskopskontraktSigneringId: number) {
        const command = new WithdrawContractCommand(virkeskopskontraktSigneringId);
        // Detta command körs alltid isolerat - oavsett vad som ligger i stacken hos commandService
        this.sendIsolatedCommand(command);
    }

    private getDigitalSignering(kontraktId: number): Observable<VirkeskopskontraktDigitalSignering[]> {
        return this.apiClientService.get<VirkeskopskontraktDigitalSignering[]>(`digitalsignering/kontrakt/${kontraktId}`);
    }

    private getDigitalsigneringUtskrifter(kontraktId: number): Observable<VKDigitalsigneringUtskrifterDTO> {
        return this.avtalService.getDokumentInfo({ parentId: kontraktId }).pipe(
            map((dokument) => ({
                utskrifter: dokument.filter((x) => x.typ !== "Kontrakt-bilaga"),
                ovrigaBilagor: dokument.filter((x) => x.typ === "Kontrakt-bilaga")
            }))
        );
    }

    private postCreateAndSendContractSrc(command: CreateAndSendContractCommand) {
        return this.apiClientService.post("digitalsignering", command).pipe(
            catchError(() => of([])) // Behövs för att inte observable ska unsubscriba
        );
    }

    private sendIsolatedCommand(command: ICommand) {
        const local = this.commandService.createInstance();
        local.addTransactionCommand(command);
        const work = local.pushCommandQueueTransaction();
        this.busyService.addWork(work).subscribe(x => {
            this.srcRefreshSigneringar$.next();
        });
    }
}
